import { useState } from "react";
import { fetchWithAuth } from "../../../../../api-client/api_client";
import { showErrorToast, showSuccessToast } from "../../../../shared/Toasts";
import { AutomatedTestSuiteExecution } from "../types";

interface Params {
  integrationID: string;
  integrationVersionId: string | null;
  onSuccess?: (execution: AutomatedTestSuiteExecution) => void;
  onError?: () => void;
}

interface KickOffIndividualTestResponse {
  execution: AutomatedTestSuiteExecution | null;
}

const useKickOffIndividualAutomatedTest = ({
  integrationID,
  integrationVersionId,
  onSuccess,
  onError: onErrorCallback,
}: Params) => {
  // state
  const [isKickingOff, setIsKickingOff] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const kickOffIndividualTest = (testSuiteClassName: string) => {
    if (!integrationID || !integrationVersionId) {
      showErrorToast("Missing integration ID or integration version ID");
      return;
    }

    if (!testSuiteClassName) {
      showErrorToast("Missing test suite class name");
      return;
    }

    setIsKickingOff(true);
    setError(null);

    fetchWithAuth({
      path: `/integrations/${integrationID}/versions/${integrationVersionId}/test-automation/kick-off/${testSuiteClassName}`,
      method: "POST",
      body: {},
      expectJSON: true,
      onResponse: (data: KickOffIndividualTestResponse) => {
        setIsKickingOff(false);
        showSuccessToast(`Successfully kicked off ${testSuiteClassName}`);

        // Call the success callback to trigger polling with the execution data
        if (onSuccess && data.execution) {
          onSuccess(data.execution);
        }
      },
      onError: (error) => {
        setIsKickingOff(false);
        if (onErrorCallback) {
          onErrorCallback();
        }

        let errorMessage = `Failed to kick off ${testSuiteClassName}`;
        if (error && typeof error === "object" && "statusText" in error) {
          const errorResponse = error as Response;
          errorMessage = `${errorMessage}: ${errorResponse.statusText}`;
        }
        setError(errorMessage);
        showErrorToast(errorMessage);
      },
    });
  };

  return {
    kickOffIndividualTest,
    isKickingOff,
    error,
  };
};

export default useKickOffIndividualAutomatedTest;
